/**
 * What the rest-day surface says, for both clients.
 *
 * The product knowledge here is the part members get wrong when it is only
 * implied: a rest day holds the streak, it does not add to it. The copy says
 * that in every state, because a member who believes rest days count will
 * eventually feel cheated by a number that did not move.
 *
 * There is no allowance, and the copy says so. Rationing rest is a game
 * mechanic, and this is a health product: somebody ill for a week should not
 * have to choose between recovering and keeping their streak. What keeps the
 * number honest is that freezing holds it rather than growing it, not scarcity.
 *
 * Vocabulary: a **rest day** is the thing a member declares, and **frozen** is
 * what it does to the streak. Splitting the two keeps the reason human and the
 * mechanic literal, and it gives the reverse action an obvious name.
 *
 * Mobile renders this in a bottom sheet and web in a modal. Neither owns a
 * word of it.
 */
/**
 * The streak length at which a rest day starts being offered. Matches the
 * streak card's own threshold for showing a flame at all, so the card and the
 * offer cannot disagree about whether a streak exists.
 */
export const REST_DAY_MIN_STREAK = 2;
/**
 * Whether to offer a rest day at all.
 *
 * Only on a live streak. At zero there is nothing to protect, and a member one
 * day in would be freezing a number that has not started yet.
 */
export function canOfferRestDay(streakDays) {
    return streakDays >= REST_DAY_MIN_STREAK;
}
export function restDayView(state, streakDays, opts = {}) {
    if (state.todayDeclared) {
        // A member can declare rest and then walk anyway. The declaration stands,
        // but saying "falling short will not break the run" to somebody who
        // already cleared their target reads as though nothing was noticed.
        if (opts.goalMetToday) {
            return {
                mode: "declared",
                title: "Your streak is frozen",
                body: "You hit your goal today anyway, so it counts as a normal goal day. The freeze is still on, and you can take it off.",
                streakNote: "Walking through a rest day earns the day. Your streak counted today either way.",
                reassurance: "Freeze again tomorrow if you need to",
                action: { kind: "unfreeze", label: "Unfreeze my streak" },
                dismissLabel: "Keep it frozen",
            };
        }
        return {
            mode: "declared",
            title: "Your streak is frozen",
            body: "Today is a rest day, so falling short will not break the run. Walk anyway if you feel like it and today counts as a normal goal day.",
            streakNote: streakDays > 0
                ? `Frozen at ${streakDays}. Unfreezing lets today count again, and break the run if you fall short.`
                : "Nothing to hold yet, but today will not count against you.",
            reassurance: "Freeze again tomorrow if you need to",
            action: { kind: "unfreeze", label: "Unfreeze my streak" },
            dismissLabel: "Keep it frozen",
        };
    }
    return {
        mode: "available",
        title: "Taking a rest day?",
        body: "Recovery is part of the work. Freeze your streak and a short day stops costing you the run.",
        streakNote: streakDays > 0
            ? // "Your streak stays at 9" rather than "your 9 days stays put": the
                // count reads the same at 1 and at 40 and needs no lowercase fragment
                // spliced onto the front of the sentence.
                `Your streak stays at ${streakDays}. Freezing holds it there and never adds a day.`
            : "Your streak holds where it is. Freezing never adds a day to the count.",
        reassurance: "No limit. Rest as often as your body needs",
        action: { kind: "freeze", label: "Freeze my streak" },
        dismissLabel: "Not today",
    };
}
//# sourceMappingURL=rest-day.js.map